import { createContext } from 'react';
import { contentProps, track, type ContentRef, type EventProps } from './analytics';

/**
 * Impression definition `imp_v1` (docs/interaction-coverage.md):
 * at least 50% of the sphere is inside the viewport, below the sticky header,
 * continuously for 1 second, while the tab is visible and the wall isn't
 * covered by the viewer (inert). Counted once per sphere per list instance —
 * a new search, sort, or collection starts a new list instance.
 */
export const IMPRESSION_DEFINITION = 'imp_v1';
const MIN_RATIO = 0.5;
const MIN_DWELL_MS = 1000;
const HEADER_MARGIN = '-72px 0px 0px 0px';

export interface ListContext {
  surface: 'home' | 'search' | 'collection';
  sort: string;
  listInstance: string;
}

export const ListAnalyticsContext = createContext<ListContext | null>(null);

interface Entry {
  content: ContentRef;
  position: number;
  list: ListContext;
  timer: number | null;
  visible: boolean;
}

const entries = new Map<Element, Entry>();
const counted = new Set<string>();
let observer: IntersectionObserver | null = null;

function eligible(el: Element): boolean {
  return document.visibilityState === 'visible' && !el.closest('[inert]');
}

function arm(el: Element, entry: Entry) {
  if (entry.timer !== null || !entry.visible || !eligible(el)) return;
  const key = `${entry.list.listInstance}:${entry.content.id}`;
  if (counted.has(key)) return;
  entry.timer = window.setTimeout(() => {
    entry.timer = null;
    if (!entry.visible || !eligible(el) || counted.has(key)) return;
    counted.add(key);
    track('content_impression', {
      ...contentProps(entry.content),
      impression_def: IMPRESSION_DEFINITION,
      position: entry.position,
      surface: entry.list.surface,
      sort: entry.list.sort,
      list_instance: entry.list.listInstance,
    });
  }, MIN_DWELL_MS);
}

function disarm(entry: Entry) {
  if (entry.timer !== null) window.clearTimeout(entry.timer);
  entry.timer = null;
}

function ensureObserver(): IntersectionObserver | null {
  if (observer || typeof IntersectionObserver === 'undefined') return observer;
  observer = new IntersectionObserver(
    (changes) => {
      for (const change of changes) {
        const entry = entries.get(change.target);
        if (!entry) continue;
        entry.visible = change.isIntersecting && change.intersectionRatio >= MIN_RATIO;
        if (entry.visible) arm(change.target, entry);
        else disarm(entry);
      }
    },
    { threshold: [0, MIN_RATIO, 1], rootMargin: HEADER_MARGIN }
  );
  document.addEventListener('visibilitychange', () => {
    for (const [el, entry] of entries) {
      if (document.visibilityState === 'visible') arm(el, entry);
      else disarm(entry);
    }
  });
  return observer;
}

/** The viewer toggles `inert` on the wall; re-check spheres that were waiting. */
export function recheckImpressions() {
  for (const [el, entry] of entries) {
    if (eligible(el)) arm(el, entry);
    else disarm(entry);
  }
}

export function observeImpression(el: Element, content: ContentRef, position: number, list: ListContext): () => void {
  const io = ensureObserver();
  if (!io) return () => undefined;
  entries.set(el, { content, position, list, timer: null, visible: false });
  io.observe(el);
  return () => {
    const entry = entries.get(el);
    if (entry) disarm(entry);
    entries.delete(el);
    io.unobserve(el);
  };
}

export function listProps(list: ListContext | null, position: number): EventProps {
  return list ? { surface: list.surface, sort: list.sort, list_instance: list.listInstance, position } : { position };
}
